import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  BentoCategory,
  OrganizationNiche,
  OrganizationNicheStatus,
} from "schemas/dashboard";
import { EmailSettingTemplate, EmailSettings } from "schemas/email";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import StyledVideo from "components/Styled/Video";
import {
  CHANGE_NICHE_PREVIEW,
  HOW_TO_CHANGE_NICHE_VIDEO_LINK,
} from "constants/influencer";
import { Template } from "features/Influencer/ContactList/schema";
import {
  BulkSendSettingsStatus,
  OrganizationBulkSendSettings,
} from "features/Influencer/Discover/Results/DiscoverFilter/AutomationVA/UpcomingPitchScheduleDialog/schema";
import { fetcherAuth } from "utils/api";
import { generateColorFromString } from "utils/color";
import { trackEvent } from "utils/tracking";

import EmailBoost from "../EmailBoost";
import EditFallbackTemplatesDrawer from "./EditFallbackTemplatesDrawer";
import EditNicheDrawer from "./EditNicheDrawer";
import GeneralSettings from "./GeneralSettings";
import NewNicheDrawer from "./NewNicheDrawer";
import StatusChip from "./StatusChip";

interface IProps {
  bulkSendSetting: OrganizationBulkSendSettings;
  setBulkSendSetting: Dispatch<
    SetStateAction<OrganizationBulkSendSettings | null>
  >;
  fallbackTemplates: Template[];
  setFallbackTemplates: Dispatch<SetStateAction<Template[]>>;
  categoriesToPickFrom: BentoCategory[];
}

const styles = {
  toggle: {
    textTransform: "none",
    border: "none",
    borderRadius: "20px !important",
    px: 2.5,
    py: 0.75,
    "&.Mui-selected": {
      backgroundColor: "#fff",
      color: "#000",
      "&:hover": { backgroundColor: "#fff" },
    },
    "&:hover": { backgroundColor: "#f1f1f1" },
  },
  drawer: {
    "& .MuiDrawer-paper": {
      position: "absolute",
      right: "-8px",
      boxShadow: "-8px 0px 16px rgba(0, 0, 0, 0.1)", // ← soft shadow
    },
  },
  toggleGroup: {
    backgroundColor: "#f8f8f6",
    borderRadius: "24px",
    p: 0.5,
  },
  card: {
    border: `1px solid #ECEAE5`,
    p: 2,
    borderRadius: "10px",
    position: "relative",
  },
  editButtonWrapper: {
    position: "absolute",
    right: "8px",
    top: "12px",
  },
  editButton: {
    fontSize: 14,
    color: grey[500],
  },
  editIcon: {
    fontSize: 14,
    color: grey[500],
  },
  closeIcon: {
    position: "absolute",
    right: "8px",
    top: "12px",
    fontSize: 18,
    color: grey[400],
  },
  link: {
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      color: "primary.main",
      textDecoration: "underline",
    },
  },
};

export enum NicheTab {
  all = "all",
  active = "active",
}

export default function VASettings({
  bulkSendSetting,
  fallbackTemplates,
  setFallbackTemplates,
  setBulkSendSetting,
  categoriesToPickFrom,
}: IProps) {
  const navigate = useNavigate();
  const { setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [editNicheIdx, setEditNicheIdx] = useState<number | null>(null);
  const [newNicheOpen, setNewNicheOpen] = useState<boolean>(false);
  const {
    templates,
    userHasNoTemplates,
    editTemplateDialogId,
    setEditTemplateDialogId,
  } = useContext(OutreachTemplatesContext);
  const [nicheTemplates, setNicheTemplates] = useState<(Template[] | null)[]>(
    [],
  );
  const [allEmailSettings, setAllEmailSettings] = useState<EmailSettings[]>([]);
  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [nicheLoading, setNicheLoading] = useState<boolean>(false);
  const [tab, setTab] = useState<NicheTab>(NicheTab.all);
  const [niches, setNiches] = useState<OrganizationNiche[]>([]);
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [openSettings, setOpenSettings] = useState<boolean>(false);
  const [openEditFallbackDrawer, setOpenEditFallbackDrawer] =
    useState<boolean>(false);

  const getNiches = async (currentTab: NicheTab) => {
    let url = `/api/organization/${currentOrg?.id}/organization-niches/v2?is_archived=false`;
    if (currentTab === NicheTab.active) {
      url += "&statuses=active";
    }

    setNicheLoading(true);
    try {
      const { niches } = await fetcherAuth(getToken, url, "GET");
      setNiches(niches);
      return niches;
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setNicheLoading(false);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/email-settings`,
        "GET",
      );
      setAllEmailSettings(res.emailSettings);
      return res.emailSettings;
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const computeNicheTemplates = (
    nichesRes: OrganizationNiche[],
    emailSettingsRes: EmailSettings[],
  ) => {
    const templatesById = new Map<number, Template>();
    for (const template of templates) {
      if (template.id) {
        templatesById.set(template.id, template);
      }
    }

    const emailSettingIdToTemplateIds = new Map<number, number[]>();
    for (const s of emailSettingsRes ?? []) {
      const templateIds = s.emailSettingTemplates?.map(
        (settingTemplate: EmailSettingTemplate) => settingTemplate.templateId,
      );
      if (s.id != null && templateIds != null) {
        emailSettingIdToTemplateIds.set(s.id, templateIds);
      }
    }

    const computedNicheTemplates: (Template[] | null)[] = (nichesRes ?? []).map(
      (niche: OrganizationNiche) => {
        const templateIds = niche.emailSettingId
          ? emailSettingIdToTemplateIds.get(niche.emailSettingId)
          : undefined;
        if (!templateIds) return null;
        return templateIds
          .map((templateId) => templatesById.get(templateId))
          .filter((t): t is Template => t !== undefined);
      },
    );

    setNicheTemplates(computedNicheTemplates);
  };

  const addNewNiche = (niche: OrganizationNiche) => {
    const newNiches = [niche, ...niches];
    const newEmailSetting = niche.emailSetting;

    if (!newEmailSetting) {
      return;
    }

    let newEmailSettings = [];
    if (
      allEmailSettings.some((setting) => setting.id === newEmailSetting?.id)
    ) {
      newEmailSettings = allEmailSettings.map((emailSetting) => {
        if (emailSetting.id === newEmailSetting?.id) {
          return newEmailSetting;
        }
        return emailSetting;
      });
    } else {
      newEmailSettings = [...allEmailSettings, newEmailSetting];
    }
    setNiches(newNiches);
    setAllEmailSettings(newEmailSettings);
    computeNicheTemplates(newNiches, newEmailSettings);
  };

  const editNiche = (niche: OrganizationNiche) => {
    if (
      tab === NicheTab.active &&
      niche.status === OrganizationNicheStatus.canceled
    ) {
      setNiches((prev) => prev.filter((n) => n.id !== niche.id));
    }
    const newNiches = niches.map((n) => {
      if (n.id === niche.id) {
        return niche;
      }
      return n;
    });
    const newEmailSetting = niche.emailSetting;

    if (!newEmailSetting) {
      return;
    }

    let newEmailSettings = [];
    if (
      allEmailSettings.some((setting) => setting.id === newEmailSetting?.id)
    ) {
      newEmailSettings = allEmailSettings.map((emailSetting) => {
        if (emailSetting.id === newEmailSetting?.id) {
          return newEmailSetting;
        }
        return emailSetting;
      });
    } else {
      newEmailSettings = [...allEmailSettings, newEmailSetting];
    }
    setNiches(newNiches);
    setAllEmailSettings(newEmailSettings);
    computeNicheTemplates(newNiches, newEmailSettings);
  };

  const loadData = async () => {
    setFetchLoading(true);
    try {
      const [nichesRes, emailSettingsRes] = await Promise.all([
        getNiches(tab),
        fetchEmailSettings(),
      ]);
      computeNicheTemplates(nichesRes, emailSettingsRes);
    } finally {
      setFetchLoading(false);
    }
  };

  const activeNiches = niches.filter(
    (niche) => niche.status === OrganizationNicheStatus.active,
  );
  const pendingNiches = niches.filter(
    (n) => n.status === OrganizationNicheStatus.pending,
  );

  const totalBrandsScheduled =
    (bulkSendSetting.numberOfBrands || 0) +
    (bulkSendSetting.numberOfRepitchBrands || 0);

  const selectedNiche = editNicheIdx !== null ? niches[editNicheIdx] : null;

  useEffect(() => {
    if ((templates.length > 0 || userHasNoTemplates) && !isFetched) {
      loadData();
      setIsFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length, userHasNoTemplates]);

  if (fetchLoading) {
    return (
      <Grid container sx={{ p: 2 }}>
        <Grid item container xs={12}>
          <Grid item xs={12} md={12} lg={8} container>
            <Grid item xs={8}>
              <Skeleton height="80px" />
            </Grid>
            <Grid item container xs={12}>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
            </Grid>
            <Grid item container xs={12} columnSpacing={1}>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
            </Grid>
            <Grid item container xs={12} columnSpacing={1}>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
            </Grid>
            <Grid item container xs={12} columnSpacing={1}>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  if (openSettings) {
    return (
      <GeneralSettings
        handleClose={() => setOpenSettings(false)}
        bulkSendSetting={bulkSendSetting}
        setBulkSendSetting={setBulkSendSetting}
      />
    );
  }

  const status =
    bulkSendSetting.status === BulkSendSettingsStatus.active &&
    activeNiches.length > 0
      ? "live 🚀"
      : bulkSendSetting.status === BulkSendSettingsStatus.inactive ||
          pendingNiches.length === 0
        ? "paused ⏸️"
        : "getting set-up 🚀";

  return (
    <Box sx={{ p: 2, pb: { xs: 10, md: 2 } }}>
      <Box
        display="flex"
        flexWrap="wrap"
        gap={3}
        sx={{ mb: 1, justifyContent: { xs: "flex-end", sm: "space-between" } }}
        alignItems="flex-start"
      >
        <Grid item xs={12} md>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              width: { xs: "100%", md: "inherit" },
            }}
          >
            Your AI Virtual Assistant is {status}
          </Typography>
          {status === "live 🚀" && (
            <Typography sx={{ fontSize: "16px" }}>
              Campaigns are running smoothly —{" "}
              <Typography
                component="span"
                sx={{ fontWeight: 600, fontSize: "inherit" }}
              >
                {activeNiches.length} niche
                {activeNiches.length !== 1 ? "s" : ""} active,{" "}
                {totalBrandsScheduled} emails to be{" "}
                <Link
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    navigate(`/${routes.inboxScheduled}`);
                  }}
                >
                  scheduled for next week
                </Link>
              </Typography>{" "}
            </Typography>
          )}
          {status === "getting set-up 🚀" && (
            <Typography sx={{ fontSize: "16px" }}>
              The set-up process is running smoothly—{" "}
              <Typography
                component="span"
                sx={{ fontWeight: 600, fontSize: "inherit" }}
              >
                {pendingNiches.length} niche
                {pendingNiches.length !== 1 ? "s" : ""} are getting set up
              </Typography>
            </Typography>
          )}
          {status === "paused ⏸️" &&
            bulkSendSetting.status === BulkSendSettingsStatus.inactive && (
              <Typography sx={{ fontSize: "16px" }}>
                Currently, your AI Assistant is paused. You can change this in{" "}
                <Link
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    setOpenSettings(true);
                  }}
                >
                  your settings
                </Link>
              </Typography>
            )}
          {status === "paused ⏸️" &&
            bulkSendSetting.status !== BulkSendSettingsStatus.inactive && (
              <Typography sx={{ fontSize: "16px" }}>
                Currently, you have no active niches. Set a niche below as
                active to start scheduling emails
              </Typography>
            )}

          <Grid item sx={{ maxWidth: 450, my: 2 }}>
            <StyledVideo
              image={CHANGE_NICHE_PREVIEW}
              videoPreview={CHANGE_NICHE_PREVIEW}
              link={HOW_TO_CHANGE_NICHE_VIDEO_LINK}
              title={`AI Virtual Assistant: How to add or change a Niche`}
            />
          </Grid>
        </Grid>
        <Button
          onClick={() => {
            setOpenSettings(true);
            trackEvent("VA Settings Page Opened Settings");
          }}
          color="inherit"
        >
          <Box
            component="span"
            className="fa-solid fa-sliders-simple"
            sx={{ mr: 1 }}
          />
          Settings
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        sx={{ mb: 2 }}
      >
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            onClick={(e: CustomEvent) => {
              e.stopPropagation();
              setNewNicheOpen(true);
              trackEvent("VA Settings Page Add Niche Clicked");
            }}
          >
            <Box component="span" className="fa-solid fa-plus" sx={{ mr: 1 }} />
            Add a niche
          </Button>
        </Box>
        <ToggleButtonGroup
          color="primary"
          value={tab}
          exclusive
          aria-label="Platform"
          sx={styles.toggleGroup}
          onChange={(_, value: NicheTab) => {
            setTab(value);
            getNiches(value);
            trackEvent("VA Settings Page Changed Tab", {
              tab: value,
            });
          }}
        >
          <ToggleButton sx={styles.toggle} value={NicheTab.all}>
            Show All Niches
          </ToggleButton>
          <ToggleButton sx={styles.toggle} value={NicheTab.active}>
            Only Active
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={1}>
        {nicheLoading && (
          <Grid container spacing={1} xs={12} sx={{ mt: -5 }}>
            <Grid xs={12} sm={6} lg={4} item>
              <Skeleton height="200px" />
            </Grid>
            <Grid xs={12} sm={6} lg={4} item>
              <Skeleton height="200px" />
            </Grid>
            <Grid xs={12} sm={6} lg={4} item>
              <Skeleton height="200px" />
            </Grid>
          </Grid>
        )}
        {!nicheLoading &&
          niches.map((niche, idx) => (
            <Grid key={niche.id} xs={12} sm={6} lg={4} item>
              <Box sx={styles.card}>
                <Box
                  display="flex"
                  gap={1}
                  flexWrap="wrap"
                  sx={{ mb: 1, mr: 2 }}
                >
                  <Chip
                    size="small"
                    sx={{
                      backgroundColor: generateColorFromString(
                        niche.name,
                        "tagColorPalette",
                      ),
                      fontSize: 14,
                      px: 1,
                    }}
                    label={niche.name}
                  />
                  {niche.status && <StatusChip status={niche.status} />}
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Outreach Template
                  </Typography>
                </Box>
                <Box>
                  <Link
                    onClick={() => {
                      const editTemplateId =
                        nicheTemplates[idx]?.[0]?.id?.toString();
                      if (!!editTemplateId) {
                        setEditTemplateDialogId(editTemplateId);
                      }
                      trackEvent("VA Settings Page Clicked Niche Template", {
                        "Template ID": editTemplateId,
                      });
                    }}
                    sx={styles.link}
                  >
                    <Typography>
                      {nicheTemplates[idx]?.[0]?.name || "Setting Up"}
                    </Typography>
                  </Link>
                </Box>
                <Tooltip
                  title={
                    niche.status === OrganizationNicheStatus.pending
                      ? "This niche cannot be edited while the status is pending. We are currently setting up your niche"
                      : null
                  }
                  enterTouchDelay={0}
                >
                  <Box sx={styles.editButtonWrapper}>
                    <IconButton
                      size="small"
                      disabled={
                        niche.status === OrganizationNicheStatus.pending
                      }
                      sx={styles.editButton}
                      onClick={(e: CustomEvent) => {
                        e.stopPropagation();
                        setEditNicheIdx(idx);
                        trackEvent("VA Settings Page Clicked Edit Niche", {
                          "Template Index": idx,
                        });
                      }}
                    >
                      <Box component="span" className="fa-regular fa-pen" />
                    </IconButton>
                  </Box>
                </Tooltip>
              </Box>
            </Grid>
          ))}
        {!nicheLoading && fallbackTemplates[0]?.name && (
          <Grid xs={12} sm={6} lg={4} item>
            <Box sx={styles.card}>
              <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1, mr: 2 }}>
                <Chip
                  size="small"
                  sx={{
                    backgroundColor: "primary.main",
                    fontSize: 14,
                    px: 1,
                    color: "white",
                    textTransform: "capitalize",
                  }}
                  label="Default Template"
                />
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Template
                </Typography>
              </Box>
              <Box>
                <Link
                  onClick={() => {
                    const editTemplateId = fallbackTemplates[0]?.id?.toString();
                    if (!!editTemplateId) {
                      setEditTemplateDialogId(editTemplateId);
                    }
                  }}
                  sx={styles.link}
                >
                  <Typography>{fallbackTemplates[0]?.name}</Typography>
                </Link>
              </Box>
              <Box sx={styles.editButtonWrapper}>
                <IconButton
                  size="small"
                  sx={styles.editButton}
                  onClick={() => {
                    setOpenEditFallbackDrawer(true);
                  }}
                >
                  <Box component="span" className="fa-regular fa-pen" />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>

      <EditNicheDrawer
        editNiche={editNiche}
        removeNiche={(niche: OrganizationNiche) => {
          setNiches((prev) => prev.filter((n) => n.id !== niche.id));
        }}
        handleClose={() => {
          if (editTemplateDialogId === null) {
            setEditNicheIdx(null);
          }
        }}
        templates={nicheTemplates[editNicheIdx || 0] || null}
        selectedNiche={selectedNiche}
        categoriesToPickFrom={categoriesToPickFrom}
      />
      {bulkSendSetting.emailSettingId && (
        <Drawer
          open={openEditFallbackDrawer}
          anchor="right"
          onClose={() => {
            setOpenEditFallbackDrawer(false);
          }}
          sx={styles.drawer}
        >
          <EditFallbackTemplatesDrawer
            handleClose={() => {
              setOpenEditFallbackDrawer(false);
            }}
            templates={fallbackTemplates || null}
            setTemplates={setFallbackTemplates}
            open={openEditFallbackDrawer}
            emailSettingId={bulkSendSetting.emailSettingId}
          />
        </Drawer>
      )}
      <NewNicheDrawer
        addNewNiche={addNewNiche}
        open={newNicheOpen}
        handleClose={() => {
          if (newNicheOpen) {
            setNewNicheOpen(false);
          }
        }}
        categoriesToPickFrom={categoriesToPickFrom}
      />

      <EmailBoost />
    </Box>
  );
}
